import { EHeaderTab } from '@/components/ui/SearchHeader/SearchHeader';
import { ESearchFilters } from '@/constants/searchFilters';
import { parseFlexibleDateRange } from '@/redux/selectors/queryParams';
import {
  trackGuidedSearchStepAnswered,
  trackRenterSearchedEvent,
} from '@/services/analytics/search';
import { IRenterSearchedInfo } from '@/services/analytics/search/types';
import { ERentalType } from '@/services/analytics/types';
import { parseEventDate } from '@/services/analytics/utils';
import { LocationGroupType } from '@/utility/surfacedLocation';

import { EGuidedSearchComponentType, TGuidedSearchComponent } from './types';

export const trackRenterSearched = (
  params: Record<string, string>,
  override?: Partial<IRenterSearchedInfo>,
) => {
  const address = params[ESearchFilters.ADDRESS];
  const from = params[ESearchFilters.DATE_FROM];
  const to = params[ESearchFilters.DATE_TO];
  const flexible = params[ESearchFilters.FLEXIBLE_DAYS];
  const adults = params[ESearchFilters.GUESTS_ADULTS];
  const children = params[ESearchFilters.GUESTS_CHILDREN];
  const feature = params[ESearchFilters.FILTER_FEATURE];
  const estimatedTripDistance = params[ESearchFilters.ESTIMATED_TRIP_DISTANCE];

  trackRenterSearchedEvent({
    source: 'guided_search_2',
    surfacedLocations: LocationGroupType.OTHER,
    totalAdults: Number(adults) || 0,
    totalChildren: Number(children) || 0,
    totalInfants: null,
    locationQuery: address || null,
    isADAAccessible: !!feature?.includes('accessible'),
    isPetFriendly: !!feature?.includes('pet_friendly'),
    flexibleDateRange: parseFlexibleDateRange(from, to, flexible),
    departureDate: (from && parseEventDate(from)) || null,
    returnDate: (to && parseEventDate(to)) || null,
    hasCategories: true,
    selectedCategory: EHeaderTab.ALL,
    towableYear: params['towable_year'] || null,
    towableMake: params['towable_make'] || null,
    towableModel: null,
    rentalType: ERentalType.RV,
    searchFilterCategory: null,
    lastQuestionSeen: null,
    estimatedMiles: estimatedTripDistance ? Number(estimatedTripDistance) : null,
    ...override,
  });
};

// Radio values are row ids; send labels. Keep inner "" so guest positions stay meaningful.
const getSelectedOptions = (
  field: TGuidedSearchComponent['fields'][number],
  value: string | string[] | undefined,
): string[] => {
  const values = (Array.isArray(value) ? value : [value]).map(v => v || '');
  while (values.length && !values[values.length - 1]) values.pop();

  if (field.component_type === EGuidedSearchComponentType.ButtonGroupRadio) {
    return values.map(id => field.properties.rows?.find(row => row.id === id)?.label ?? id);
  }
  return values;
};

// stepIndex is 1-based.
export const trackGuidedSearchStepAnswers = (
  step: TGuidedSearchComponent,
  stepIndex: number,
  stepSelection: Record<string, string | string[] | undefined> | undefined,
  source: IRenterSearchedInfo['source'],
) => {
  if (!stepSelection) return;

  step.fields.forEach(field => {
    const selectedOptions = getSelectedOptions(field, stepSelection[field.id]);
    if (!selectedOptions.length) return;

    trackGuidedSearchStepAnswered({
      source: source ?? '',
      stepIndex,
      stepId: step.id,
      fieldId: field.id,
      componentType: field.component_type,
      selectedOptions,
    });
  });
};
